import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { InputTextarea } from 'primereact/inputtextarea';
import LoadingComponent from '../loadingComponent';
import Service from '../../services';
import { baseUrlAdmin } from '../../store/apiConstants';
import Authorizer from '../session/authorizer';
import { PERMISSIONS } from '../../session/authorize';
import './OverrideApprovals.scss';

/**
 * Override Approvals Component
 * Displays pending override requests for approval
 */
class OverrideApprovals extends Component {
    constructor(props) {
        super(props);
        this.state = {
            pendingApprovals: [],
            loading: false,
            selectedOverride: null,
            showApprovalDialog: false,
            showRejectDialog: false,
            showImpactDialog: false,
            impactAnalysis: null,
            remarks: '',
            rejectionReason: '',
            rejectSubmitted: false
        };
        this.service = new Service();
        this.toast = React.createRef();
    }

    componentDidMount() {
        this.fetchPendingApprovals();
    }

    fetchPendingApprovals = async () => {
        this.setState({ loading: true, pendingApprovals: [] });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const url = `${baseUrlAdmin}/fee-decision/governance/overrides/pending-approvals?academicYear=${academicYear}&_t=${new Date().getTime()}`;
            
            const response = await this.service.get(url, true);
            
            if (response && response.status) {
                const data = response.res?.data || [];

                this.setState({ pendingApprovals: data });
            } else {

                this.showToast('error', 'Error', response?.errMessage || 'Failed to fetch pending approvals');
            }
        } catch (error) {
            console.error('Error fetching pending approvals:', error);
            this.showToast('error', 'Error', 'Failed to fetch pending approvals');
        } finally {
            this.setState({ loading: false });
        }
    }

    viewImpactAnalysis = async (override) => {
        this.setState({ loading: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const url = `${baseUrlAdmin}/fee-decision/governance/overrides/impact-analysis`;
            
            const payload = {
                academicYear,
                studentId: override.studentId,
                currentBand: override.impactAnalysis?.currentRiskBand,
                newBand: override.overrideBand
            };
            
            const response = await this.service.post(url, payload, true);
            
            if (response && response.status) {
                this.setState({ 
                    impactAnalysis: response.res.data,
                    selectedOverride: override,
                    showImpactDialog: true 
                });
            } else {
                this.showToast('error', 'Error', response?.errMessage || 'Failed to fetch impact analysis');
            }
        } catch (error) {
            console.error('Error fetching impact analysis:', error);
            this.showToast('error', 'Error', 'Failed to fetch impact analysis');
        } finally {
            this.setState({ loading: false });
        }
    }

    approveOverride = async () => {
        this.setState({ loading: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const { selectedOverride, remarks } = this.state;
            
            const url = `${baseUrlAdmin}/fee-decision/governance/overrides/approve/${selectedOverride.studentId}`;
            
            const payload = {
                academicYear,
                remarks
            };
            
            const response = await this.service.post(url, payload, true);
            
            if (response && response.status) {
                this.showToast('success', 'Success', 'Override approved successfully');
                this.setState({ showApprovalDialog: false, remarks: '', selectedOverride: null });
                await this.fetchPendingApprovals();
            } else {
                this.showToast('error', 'Error', response?.errMessage || 'Failed to approve override');
            }
        } catch (error) {
            console.error('Error approving override:', error);
            this.showToast('error', 'Error', 'Failed to approve override');
        } finally {
            this.setState({ loading: false });
        }
    }

    rejectOverride = async () => {
        this.setState({ loading: true, rejectSubmitted: true });
        try {
            const academicYear = localStorage.getItem('userAcademicYear');
            const { selectedOverride, rejectionReason } = this.state;
            
            if (!rejectionReason || rejectionReason.trim() === '') {
                this.showToast('warn', 'Warning', 'Please provide a rejection reason');
                this.setState({ loading: false });
                return;
            }
            
            const url = `${baseUrlAdmin}/fee-decision/governance/overrides/reject/${selectedOverride.studentId}`;
            
            const payload = {
                academicYear,
                rejectionReason
            };
            
            const response = await this.service.post(url, payload, true);
            
            if (response && response.status) {
                this.showToast('success', 'Success', 'Override rejected successfully');
                this.setState({ showRejectDialog: false, rejectionReason: '', selectedOverride: null });
                await this.fetchPendingApprovals();
            } else {
                this.showToast('error', 'Error', response?.errMessage || 'Failed to reject override');
            }
        } catch (error) {
            console.error('Error rejecting override:', error);
            this.showToast('error', 'Error', 'Failed to reject override');
        } finally {
            this.setState({ loading: false });
        }
    }

    showToast = (severity, summary, detail) => {
        this.toast.current?.show({
            severity: severity,
            summary: summary,
            detail: detail,
            life: 3000
        });
    }

    riskBandTemplate = (rowData) => {
        
        const getSeverity = (band) => {
            if (band === 'CRITICAL') return 'danger';
            if (band === 'HIGH') return 'warning';
            if (band === 'LOW') return 'success';
            return null; // For medium/custom
        };
        
        const renderTag = (band) => {
             if (band === 'MEDIUM') {
                 return <Tag value={band} style={{backgroundColor: '#FEF9C3', color: '#854D0E'}} />;
             }
             return <Tag severity={getSeverity(band)} value={band || 'N/A'} />;
        }
        
        return (
            <div>
                {renderTag(rowData.impactAnalysis?.currentRiskBand)}
                <span className="p-mx-2">→</span>
                {renderTag(rowData.overrideBand)}
            </div>
        );
    }

    requestedByTemplate = (rowData) => {
        const date = rowData.requestedBy?.requestedAt ? new Date(rowData.requestedBy.requestedAt) : null;
        return (
            <div>
                <div className="requester-name">{rowData.requestedBy?.userName}</div>
                <div className="requester-role">{rowData.requestedBy?.role}</div>
                <div className="request-date">{date ? date.toLocaleDateString('en-GB') + ' ' + date.toLocaleTimeString('en-GB', {hour:'2-digit', minute:'2-digit'}) : '-'}</div>
            </div>
        );
    }

    actionsTemplate = (rowData) => {
        return (
            <div className="action-buttons">
                <Authorizer permId={PERMISSIONS.FDE_VIEW_IMPACT_ANALYSIS}>
                    <Button 
                        icon="pi pi-chart-bar" 
                        className="p-button-info p-button-sm p-mr-2"
                        style={{ backgroundColor: '#076EFF', borderColor: '#076EFF' }}
                        onClick={() => this.viewImpactAnalysis(rowData)}
                        tooltip="View Impact Analysis"
                    />
                </Authorizer>
                <Authorizer permId={PERMISSIONS.FDE_APPROVE_OVERRIDE}>
                    <Button 
                        icon="pi pi-check" 
                        className="p-button-success p-button-sm p-mr-2"
                        style={{ backgroundColor: '#268900', borderColor: '#268900' }}
                        onClick={() => this.setState({ selectedOverride: rowData, showApprovalDialog: true })}
                        tooltip="Approve"
                    />
                </Authorizer>
                <Authorizer permId={PERMISSIONS.FDE_REJECT_OVERRIDE}>
                    <Button 
                        icon="pi pi-times" 
                        className="p-button-danger p-button-sm"
                        style={{ backgroundColor: '#BF0031', borderColor: '#BF0031' }}
                        onClick={() => this.setState({ selectedOverride: rowData, showRejectDialog: true })}
                        tooltip="Reject"
                    />
                </Authorizer>
            </div>
        );
    }

    renderImpactDialog() {
        const { showImpactDialog, impactAnalysis } = this.state;
        
        const renderRiskTag = (band) => {
            let severity = null;
            let style = null;
            
            if (band === 'CRITICAL') style = { backgroundColor: '#FFA6BC', color: '#BF0031' };
            else if (band === 'HIGH') style = { backgroundColor: '#FFEDD5', color: '#C2410C' }; // Explicit Dark Orange
            else if (band === 'LOW') style = { backgroundColor: '#DCFCE7', color: '#268900' };
            else if (band === 'MEDIUM') {
                style = { backgroundColor: '#FEF9C3', color: '#854D0E' }; // Explicit Yellow
            } else {
                style = { backgroundColor: '#E6E8EA', color: '#4B5563' };
            }
            
            return <Tag severity={severity} value={band} style={style} className="risk-tag-large" />;
        };
        
        const getPredictionChange = (before, after) => {
            const diff = after - before;
            return diff;
        };
        
        return (
            <Dialog
                header="Override Impact Analysis"
                visible={showImpactDialog}
                style={{ width: '650px' }}
                onHide={() => this.setState({ showImpactDialog: false, impactAnalysis: null })}
            >
                {impactAnalysis && (
                    <div className="impact-analysis">
                        {/* Risk Band Change Section */}
                        <div className="analysis-section risk-change-section">
                            <div className="section-header">
                                <i className="pi pi-arrow-right-arrow-left"></i>
                                <h3>Risk Band Change</h3>
                            </div>
                            <div className="risk-change-visual">
                                <div className="risk-tag-wrapper">
                                    {renderRiskTag(impactAnalysis.currentRiskBand)}
                                    <span className="risk-label">Current</span>
                                </div>
                                <div className="arrow-icon">
                                    <i className="pi pi-arrow-right"></i>
                                </div>
                                <div className="risk-tag-wrapper">
                                    {renderRiskTag(impactAnalysis.newRiskBand)}
                                    <span className="risk-label">Proposed</span>
                                </div>
                            </div>
                        </div>
                        
                        {/* Affected Actions Section */}
                        <div className="analysis-section">
                            <div className="section-header">
                                <i className="pi pi-bolt"></i>
                                <h3>Affected Actions ({impactAnalysis.affectedActions?.length || 0})</h3>
                            </div>
                            {impactAnalysis.affectedActions?.length > 0 ? (
                                <div className="actions-list">
                                    {impactAnalysis.affectedActions.map((action, index) => (
                                        <div key={index} className="action-item">
                                            <div className="action-type">
                                                <i className="pi pi-phone"></i>
                                                <span>{action.actionType}</span>
                                            </div>
                                            <div className="action-details">
                                                <span className="action-status">{action.status}</span>
                                                <Tag 
                                                    severity={action.impact === 'CANCELLED' ? 'danger' : 'info'} 
                                                    value={action.impact} 
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    <i className="pi pi-check-circle"></i>
                                    <p>No pending actions will be affected</p>
                                </div>
                            )}
                        </div>
                        
                        {/* Payment Predictions Section */}
                        <div className="analysis-section">
                            <div className="section-header">
                                <i className="pi pi-chart-line"></i>
                                <h3>Payment Prediction Changes</h3>
                            </div>
                            
                            {impactAnalysis.predictionChanges?.outstandingAmount > 0 && (
                                <div className="p-3 mb-2 bg-blue-50 border-round text-sm">
                                    <span className="font-bold text-700">Total Outstanding: </span>
                                    <span className="text-primary font-bold">₹{impactAnalysis.predictionChanges.outstandingAmount.toLocaleString('en-IN')}</span>
                                    <p className="m-0 mt-1 text-600 text-xs text-italic">
                                        *Higher probability implies better expected recovery of this amount.
                                    </p>
                                </div>
                            )}

                            <div className="predictions-list">
                                {[
                                    { 
                                        label: '30-Day', 
                                        before: impactAnalysis.predictionChanges?.before30Day || 0, 
                                        after: impactAnalysis.predictionChanges?.after30Day || 0,
                                        amtAfter: impactAnalysis.predictionChanges?.after30Amt
                                    },
                                    { 
                                        label: '60-Day', 
                                        before: impactAnalysis.predictionChanges?.before60Day || 0, 
                                        after: impactAnalysis.predictionChanges?.after60Day || 0,
                                        amtAfter: impactAnalysis.predictionChanges?.after60Amt
                                    },
                                    { 
                                        label: '90-Day', 
                                        before: impactAnalysis.predictionChanges?.before90Day || 0, 
                                        after: impactAnalysis.predictionChanges?.after90Day || 0,
                                        amtAfter: impactAnalysis.predictionChanges?.after90Amt
                                    }
                                ].map((pred, index) => {
                                    const change = getPredictionChange(pred.before, pred.after);
                                    return (
                                        <div key={index} className="prediction-row">
                                            <div className="flex flex-column gap-1">
                                                <div className="prediction-label">
                                                    <i className="pi pi-calendar"></i>
                                                    <span>{pred.label} Probability</span>
                                                </div>
                                                {pred.amtAfter > 0 && (
                                                     <div className="text-xs text-600 ml-4 pl-1">
                                                        Est. Recovery: <span className="font-bold text-green-600">₹{pred.amtAfter.toLocaleString('en-IN')}</span>
                                                     </div>
                                                )}
                                            </div>
                                            <div className="prediction-values">
                                                <span className="value-before">{pred.before.toFixed(1)}%</span>
                                                <i className={`pi pi-arrow-right prediction-arrow ${change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral'}`}></i>
                                                <span className="value-after">{pred.after.toFixed(1)}%</span>
                                                {change !== 0 && (
                                                    <span className={`change-badge ${change > 0 ? 'positive' : 'negative'}`}>
                                                        {change > 0 ? '+' : ''}{change.toFixed(1)}%
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>
        );
    }

    render() {
        const { pendingApprovals, loading, showApprovalDialog, showRejectDialog, selectedOverride, remarks, rejectionReason, rejectSubmitted } = this.state;
        
        return (
            <div className="override-approvals-container">
                <Toast ref={this.toast} position="bottom-right" />
                
                <div className="page-header">
                    <h2>Override Approval Requests</h2>
                    <Button 
                        icon="pi pi-refresh" 
                        label="Refresh"
                        onClick={this.fetchPendingApprovals}
                        className="p-button-outlined"
                    />
                </div>
                
                <DataTable
                    value={pendingApprovals}
                    emptyMessage="No pending approvals"
                    className="approvals-table"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                >
                    <Column field="studentName" header="Student Name" sortable style={{ minWidth: '200px' }} />
                    <Column field="admissionNo" header="Admission No" sortable />
                    <Column header="Risk Band Change" body={this.riskBandTemplate} />
                    <Column field="justification" header="Justification" style={{ maxWidth: '300px' }} />
                    <Column header="Requested By" body={this.requestedByTemplate} />
                    <Column header="Actions" body={this.actionsTemplate} style={{ width: '150px' }} />
                </DataTable>
                
                {/* Approval Dialog */}
                <Dialog
                    header={() => (
                        <div className='mt-2'>
                            <h3 className="text-center text-black-alpha-90 font-bold">Approve Override Request</h3>
                        </div>
                    )}
                    visible={showApprovalDialog}
                    style={{ width: '500px' }}
                    onHide={() => this.setState({ showApprovalDialog: false, remarks: '' })}
                    footer={() => (
                        <div className="flex justify-content-center mb-4">
                            <Button 
                                label="Approve" 
                                className="confirmDialogAcceptBtn mr-4 px-6"
                                style={{ backgroundColor: '#268900', borderColor: '#268900' }}
                                onClick={this.approveOverride} 
                                autoFocus
                            />
                            <Button 
                                label="Cancel" 
                                className="confirmDialogCancelBtn px-6"
                                style={{ backgroundColor: '#E6E8EA', borderColor: '#E6E8EA', color: '#000000' }}
                                onClick={() => this.setState({ showApprovalDialog: false, remarks: '' })} 
                            />
                        </div>
                    )}
                >
                    <div className="approval-dialog-content">
                        <div className="confirmation-message mb-3">
                            <p className="font-bold text-center">Are you sure you want to approve this override request?</p>
                        </div>
                        {selectedOverride && (
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <span className="font-bold block mb-1">Student:</span>
                                    <span>{selectedOverride.studentName}</span>
                                </div>
                                <div className="col-12 md:col-6">
                                    <span className="font-bold block mb-1">Admission No:</span>
                                    <span>{selectedOverride.admissionNo}</span>
                                </div>
                                <div className="col-12 mt-2">
                                    <span className="font-bold block mb-1">Justification:</span>
                                    <span>{selectedOverride.justification}</span>
                                </div>
                            </div>
                        )}
                        <div className="field mt-3">
                            <label htmlFor="remarks" className="block mb-2 font-bold">Approval Remarks (Optional)</label>
                            <InputTextarea
                                id="remarks"
                                value={remarks}
                                onChange={(e) => this.setState({ remarks: e.target.value })}
                                rows={3}
                                className="w-full"
                                placeholder="Add any remarks or conditions..."
                            />
                        </div>
                    </div>
                </Dialog>
                
                {/* Reject Dialog */}
                <Dialog
                    header={() => (
                        <div className='mt-2'>
                            <h3 className="text-center text-black-alpha-90 font-bold">Reject Override Request</h3>
                        </div>
                    )}
                    visible={showRejectDialog}
                    style={{ width: '500px' }}
                    onHide={() => this.setState({ showRejectDialog: false, rejectionReason: '', rejectSubmitted: false })}
                    footer={() => (
                        <div className="flex justify-content-center mb-4">
                            <Button 
                                label="Reject" 
                                className="confirmDialogAcceptBtn mr-4 px-6"
                                style={{ backgroundColor: '#BF0031', borderColor: '#BF0031' }}
                                onClick={this.rejectOverride} 
                                autoFocus
                            />
                            <Button 
                                label="Cancel" 
                                className="confirmDialogCancelBtn px-6"
                                style={{ backgroundColor: '#E6E8EA', borderColor: '#E6E8EA', color: '#000000' }}
                                onClick={() => this.setState({ showRejectDialog: false, rejectionReason: '', rejectSubmitted: false })} 
                            />
                        </div>
                    )}
                >
                    <div className="reject-dialog-content">
                        <div className="warning-message mb-3">
                            <p className="font-bold text-center">Please provide a reason for rejection:</p>
                        </div>
                        <div className="field">
                            <label htmlFor="rejectionReason" className="block mb-2 font-bold">Rejection Reason <span className="text-red-500">*</span></label>
                            <InputTextarea
                                id="rejectionReason"
                                value={rejectionReason}
                                onChange={(e) => this.setState({ rejectionReason: e.target.value })}
                                rows={4}
                                placeholder="Explain why this override is being rejected..."
                                required
                                className={`w-full ${rejectSubmitted && !rejectionReason ? 'p-invalid' : ''}`}
                            />
                            {rejectSubmitted && !rejectionReason && <small className="p-error block mt-1">This field is required</small>}
                        </div>
                    </div>
                </Dialog>
                
                {this.renderImpactDialog()}
                
                {loading && <LoadingComponent />}
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    authenticated: state.authenticated
});

export default connect(mapStateToProps)(OverrideApprovals);
